import CancelIcon from '@mui/icons-material/Cancel';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, CircularProgress, Divider, FormControl, IconButton, InputAdornment, InputLabel, List, ListItemButton, ListItemIcon, ListItemText, OutlinedInput } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAdminStations } from '../../../hooks/useAdminStations';

const STALE_AFTER_HOURS = 24;

const lastObservationLabel = (active: boolean, lastObservation?: string) => {
    if (!active) return 'INACTIVE';

    return lastObservation ? dayjs(lastObservation).format('MMM D, YYYY h:mm A') : 'never';
};

// An active station quiet for a day, or one that has never reported at all, is bolded so it
// stands out from the stations still feeding. An inactive station is not expected to report,
// so it is never bolded.
const isStale = (active: boolean, lastObservation?: string) =>
    active && (!lastObservation || dayjs().diff(dayjs(lastObservation), 'hour') >= STALE_AFTER_HOURS);

export const StationList = () => {
    const [filter, setFilter] = useState<string>('');
    const { data: stations = [], isFetched } = useAdminStations();
    const navigate = useNavigate();

    const getFilteredStations = () => {
        if (filter.length > 0) {
            const filterRegex = new RegExp(filter, 'gi');

            return stations.filter(({ stationId, name, description }) => {
                return `${stationId} ${name} ${description}`.match(filterRegex);
            });
        }

        return stations.sort((a, b) => (a.sortOrder < b.sortOrder ? -1 : 1));
    };

    const handleSelectedStation = (id: string) => {
        navigate(`/admin/stations/${id}`);
    };

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 3, maxWidth: (theme) => theme.breakpoints.values.sm }}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel htmlFor="filter-stations">Filter Stations</InputLabel>
                        <OutlinedInput
                            id="filter-stations"
                            fullWidth
                            label="Filter Stations"
                            value={filter}
                            onChange={(event) => setFilter(event.target.value)}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton aria-label={'clear the filter'} edge="end" onClick={() => setFilter('')}>
                                        <CancelIcon />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Box>
                <Divider />
                <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {!isFetched && <CircularProgress />}
                    {isFetched && (
                        <List sx={{ width: '100%', height: '100%', overflowY: 'scroll', p: 0 }}>
                            {getFilteredStations().map((station) => {
                                const { _id, stationId, name, description, lastObservation, active } = station;

                                return (
                                    <ListItemButton key={_id.toString()} divider={true} onClick={() => handleSelectedStation(_id.toString())}>
                                        <ListItemText
                                            primary={`${stationId}`}
                                            secondary={
                                                <>
                                                    <Box component="span" sx={{ display: 'block' }}>{`${name} - ${description}`}</Box>
                                                    <Box
                                                        component="span"
                                                        sx={{ display: 'block', fontWeight: isStale(active, lastObservation) ? 600 : 'inherit' }}
                                                    >{`Last observation: ${lastObservationLabel(active, lastObservation)}`}</Box>
                                                </>
                                            }
                                        />
                                        <ListItemIcon>
                                            <NavigateNextIcon />
                                        </ListItemIcon>
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
